import React, { useState, useEffect, useContext, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { InternalLayout } from "../Components/InternalLayout";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Announcement as AnnouncementIcon,
  Image as ImageIcon,
  SelectAll as SelectAllIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext";

function MensagensPadrao() {
  const { user, token } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mensagens, setMensagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingMensagem, setEditingMensagem] = useState(null);
  const [mensagemToDelete, setMensagemToDelete] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    type: "info",
  });
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    endingDate: "",
  });

  // Função para mostrar alertas
  const showAlert = (message, type = "info") => {
    setAlert({ open: true, message, type });
    setTimeout(
      () => setAlert({ open: false, message: "", type: "info" }),
      5000,
    );
  };

  const fetchMensagens = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("/api/secretaria/mensagens"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMensagens(data.mensagens);
      } else {
        showAlert("Erro ao carregar mensagens", "error");
      }
    } catch (error) {
      console.error("Erro ao buscar mensagens:", error);
      showAlert("Erro ao carregar mensagens", "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user && token) {
      fetchMensagens();
    }
  }, [user, token, fetchMensagens]);

  // Verificar se é admin
  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Acesso negado. Esta página é apenas para usuários autenticados.
        </Alert>
      </Box>
    );
  }

  const copiarTexto = async (mensagem) => {
    try {
      await navigator.clipboard.writeText(mensagem?.content || "");

      // Remove o feedback após 2 segundos
      setTimeout(() => 2000);
      showAlert("Conteúdo copiado para a área de transferência!", "success");
    } catch (err) {
      console.error("Erro ao copiar mensagem:", err);
      // Fallback para navegadores que não suportam clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = mensagem?.content || "";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const handleOpenDialog = (mensagemToEdit = null) => {
    if (mensagemToEdit) {
      setEditingMensagem(mensagemToEdit);
      setFormData({
        title: mensagemToEdit.title,
        content: mensagemToEdit.content,
      });
    } else {
      setEditingMensagem(null);
      setFormData({
        title: "",
        content: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMensagem(null);
    setFormData({
      title: "",
      content: "",
    });
  };
  const handleOpenDeleteDialog = (mensagem) => {
    setMensagemToDelete(mensagem);
    setDeleteDialogOpen(true);
  };

  const handleInputChange = (field) => (event) => {
    if (field === "active") {
      setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    }
  };

  const handleSubmit = async () => {
    try {
      const method = editingMensagem ? "PUT" : "POST";
      const body = editingMensagem
        ? { mensagemId: editingMensagem._id, ...formData }
        : formData;

      const response = await fetch(getApiUrl("/api/secretaria/mensagens"), {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        showAlert(data.message, "success");
        handleCloseDialog();
        fetchMensagens();
      } else {
        const error = await response.json();
        showAlert(error.message || "Erro ao salvar mensagem", "error");
      }
    } catch (error) {
      console.error("Erro ao salvar mensagem:", error);
      showAlert("Erro ao salvar mensagem", "error");
    }
  };

  const handleDeleteMensagem = async () => {
    try {
      const response = await fetch(getApiUrl("/api/secretaria/mensagens"), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mensagemId: mensagemToDelete._id }),
      });

      if (response.ok) {
        const data = await response.json();
        showAlert(data.message, "success");
        setDeleteDialogOpen(false);
        setMensagemToDelete(null);
        fetchMensagens();
      } else {
        const error = await response.json();
        showAlert(error.message || "Erro ao remover mensagem", "error");
      }
    } catch (error) {
      console.error("Erro ao remover mensagem:", error);
      showAlert("Erro ao remover mensagem", "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <InternalLayout>
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
          pt: { xs: "56px", sm: "64px" },
          pb: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Alert */}
        {alert.open && (
          <Alert
            severity={alert.type}
            sx={{ mb: 2 }}
            onClose={() => setAlert({ open: false, message: "", type: "info" })}
          >
            {alert.message}
          </Alert>
        )}

        {/* Cabeçalho */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 0 },
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: { xs: "1.5rem", md: "2.125rem" },
            }}
          >
            <AnnouncementIcon sx={{ fontSize: { xs: 24, md: 32 } }} /> Gerenciar
            Mensagens Padrão
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              color="primary"
              sx={{
                width: { xs: "100%", sm: "auto" },
                py: { xs: 1, md: 1.5 },
              }}
            >
              Nova Mensagem
            </Button>
          </Box>
        </Box>

        {/* Estatísticas */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}
              >
                Total de Mensagens
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: "1.75rem", md: "3rem" } }}
              >
                {mensagens.length}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Layout Mobile - Cards */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {mensagens.map((mensagem) => {
            const isExpiringSoon =
              mensagem.endingDate &&
              new Date(mensagem.endingDate) <=
                new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

            return (
              <Card
                key={mensagem._id}
                sx={{
                  mb: 2,
                  border: isExpiringSoon ? "2px solid #ff9800" : undefined,
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        flex: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", flex: 1 }}
                      >
                        {mensagem.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        onClick={() => copiarTexto(mensagem)}
                        color="primary"
                        size="small"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenDialog(mensagem)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenDeleteDialog(mensagem)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {mensagem.content}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  ></Box>
                </CardContent>
              </Card>
            );
          })}
          {mensagens.length === 0 && (
            <Card>
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  Nenhuma mensagem encontrada
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Layout Desktop - Tabela */}
        <TableContainer
          component={Paper}
          sx={{ overflowX: "auto", display: { xs: "none", md: "block" } }}
        >
          <Table sx={{ minWidth: "auto" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Título</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Conteúdo</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mensagens.length > 0 ? (
                mensagens.map((mensagem) => {
                  const isExpiringSoon =
                    mensagem.endingDate &&
                    new Date(mensagem.endingDate) <=
                      new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

                  return (
                    <TableRow
                      key={mensagem._id}
                      hover
                      sx={{
                        backgroundColor: isExpiringSoon ? "#fff3e0" : undefined,
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {mensagem.title}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 300, whiteSpace: "pre-wrap" }}>
                        <Typography variant="body2">
                          {mensagem.content}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            onClick={() => copiarTexto(mensagem)}
                            color="primary"
                            size="small"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleOpenDialog(mensagem)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleOpenDeleteDialog(mensagem)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhuma mensagem ativa encontrada
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog para criar/editar mensagem */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              mx: { xs: 1, sm: 2 },
              my: { xs: 2, sm: 4 },
              maxHeight: { xs: "calc(100vh - 32px)", sm: "calc(100vh - 64px)" },
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            {editingMensagem ? "Editar Mensagem" : "Nova Mensagem"}
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2, sm: 2.5 },
                mt: 1,
              }}
            >
              <TextField
                label="Título"
                value={formData.title}
                onChange={handleInputChange("title")}
                fullWidth
                required
              />
              <TextField
                label="Conteúdo"
                value={formData.content}
                onChange={handleInputChange("content")}
                fullWidth
                required
                multiline
                rows={4}
                helperText="Descreva o conteúdo da mensagem"
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              pb: { xs: 2, sm: 3 },
              gap: { xs: 1, sm: 1 },
            }}
          >
            <Button onClick={handleCloseDialog} fullWidth={isMobile}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              fullWidth={isMobile}
              disabled={!formData.title || !formData.content}
            >
              {editingMensagem ? "Salvar" : "Criar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmação para deletar */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              mx: { xs: 1, sm: 2 },
              my: { xs: 4, sm: 8 },
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>Confirmar Remoção</DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Typography variant="body2">
              Tem certeza que deseja remover a mensagem{" "}
              <strong>{mensagemToDelete?.title}</strong>? Esta ação não pode ser
              desfeita.
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              pb: { xs: 2, sm: 3 },
              gap: { xs: 1, sm: 1 },
            }}
          >
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteMensagem}
              color="error"
              variant="contained"
              fullWidth={isMobile}
            >
              Remover
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </InternalLayout>
  );
}

export default MensagensPadrao;
